import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit3, History, ArrowRightLeft, RotateCcw, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import StrategyDialog from "@/pages/StrategyDialog";
import FictionalChart from "@/components/FictionalChart";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL;

const TRADE_LABEL = { scalp: "Scalp", daytrade: "Daytrade", swingtrade: "Swingtrade", hodl: "Hodl" };
const CONTENT_FIELDS = [
  { key: "name", label: "Name" },
  { key: "trade_types", label: "Trade-Art" },
  { key: "rough_strategy", label: "Grobe Strategie" },
  { key: "sl_description", label: "SL-Idee" },
  { key: "tp_description", label: "TP-Idee" },
  { key: "additional_notes", label: "Ergänzungen" },
];

const fmtValue = (k, v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (k === "trade_types" && Array.isArray(v)) {
    return v.map((t) => TRADE_LABEL[t] || t).join(", ") || "—";
  }
  return String(v);
};

const eq = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((x, i) => x === b[i]);
  }
  return (a ?? "") === (b ?? "");
};

export default function StrategyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState("form"); // form | reflect
  const [selected, setSelected] = useState([]); // version_no[], max 2
  const [revertTarget, setRevertTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/strategies/${id}`, { withCredentials: true });
      setStrategy(res.data);
    } catch {
      toast.error("Strategie nicht gefunden");
      navigate("/app/strategien");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const versions = useMemo(() => strategy?.versions || [], [strategy]);
  const current = useMemo(() => {
    if (!strategy) return null;
    return {
      version_no: "current",
      saved_at: strategy.updated_at,
      change_note: "Aktueller Stand",
      snapshot: CONTENT_FIELDS.reduce((acc, f) => {
        acc[f.key] = strategy[f.key];
        return acc;
      }, { has_screenshot: strategy.has_screenshot }),
    };
  }, [strategy]);

  const toggleSelect = (vNo) => {
    setSelected((prev) => {
      if (prev.includes(vNo)) return prev.filter((x) => x !== vNo);
      if (prev.length >= 2) return [prev[1], vNo];
      return [...prev, vNo];
    });
  };

  const resolveSnapshot = (vNo) => {
    if (vNo === "current") return current?.snapshot;
    return versions.find((v) => v.version_no === vNo)?.snapshot;
  };

  const diffPair = selected.length === 2 ? selected : null;

  const doRevert = async () => {
    if (revertTarget == null) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/strategies/${id}/versions/revert/${revertTarget}`,
        {},
        { withCredentials: true }
      );
      setStrategy(res.data);
      toast.success(`Zurückgesetzt auf Version ${revertTarget}`, {
        description: "Der aktuelle Stand wurde zusätzlich als neue Version gespeichert.",
      });
      setSelected([]);
    } catch (e) {
      toast.error("Zurücksetzen fehlgeschlagen", {
        description: e?.response?.data?.detail || "Bitte erneut versuchen.",
      });
    } finally {
      setRevertTarget(null);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-10 text-center" data-testid="strategy-detail-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }
  if (!strategy) return null;

  return (
    <div data-testid="strategy-detail">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full h-9 px-3"
            onClick={() => navigate("/app/strategien")}
            data-testid="strategy-detail-back"
          >
            <ArrowLeft className="mr-1 h-4 w-4" strokeWidth={1.5} /> Strategien
          </Button>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Strategie</div>
            <h1 className="font-heading text-3xl">{strategy.name}</h1>
          </div>
          <Badge
            variant="secondary"
            className={`rounded-full border capitalize ${
              strategy.status === "aktiv"
                ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border-[hsl(122_39%_49%/0.35)]"
                : strategy.status === "archiviert"
                ? "bg-secondary text-muted-foreground border-border"
                : "bg-[hsl(32_60%_55%/0.15)] text-[hsl(32_60%_72%)] border-[hsl(32_60%_55%/0.4)]"
            }`}
          >
            {strategy.status}
          </Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <Button
          className="rounded-full h-11 px-5"
          onClick={() => { setEditMode("form"); setEditOpen(true); }}
          data-testid="strategy-detail-edit"
        >
          <Edit3 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Weiterbearbeiten
        </Button>
        <Button
          variant="outline"
          className="rounded-full h-11 px-5"
          onClick={() => { setEditMode("reflect"); setEditOpen(true); }}
          data-testid="strategy-detail-coach"
        >
          <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} /> KI-Coach öffnen
        </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <section className="lg:col-span-3 space-y-4">
          <div className="card-surface p-5">
            <div className="overline mb-2">Aktueller Stand</div>
            <dl className="grid grid-cols-[130px_1fr] gap-x-3 gap-y-2 text-sm">
              {CONTENT_FIELDS.map((f) => (
                <FieldRow key={f.key} label={f.label} value={fmtValue(f.key, strategy[f.key])} />
              ))}
            </dl>
          </div>
          <FictionalChart
            seed={strategy.chart_seed}
            tradeTypes={strategy.trade_types}
            hasSL={!!strategy.sl_description}
            hasTP={!!strategy.tp_description}
            hasScreenshot={strategy.has_screenshot}
          />
          {strategy.has_screenshot && strategy.chart_screenshot && (
            <div>
              <div className="overline mb-1">Dein Screenshot</div>
              <img
                src={strategy.chart_screenshot}
                alt="Hochgeladener Chart"
                className="rounded-lg border border-border max-h-60"
              />
            </div>
          )}
        </section>

        <aside className="lg:col-span-2 space-y-4">
          <div className="card-surface p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-primary-foreground/80" strokeWidth={1.5} />
              <div className="overline">Versionsverlauf</div>
              <span className="text-xs text-muted-foreground ml-auto">
                {versions.length} + aktuell
              </span>
            </div>

            <p className="text-xs text-muted-foreground mb-3">
              Klick auf zwei Einträge, um sie zu vergleichen.
            </p>

            <ul className="space-y-2" data-testid="strategy-versions-list">
              <VersionRow
                item={current}
                isCurrent
                isSelected={selected.includes("current")}
                onSelect={() => toggleSelect("current")}
              />
              {[...versions].reverse().map((v) => (
                <VersionRow
                  key={v.version_no}
                  item={v}
                  isSelected={selected.includes(v.version_no)}
                  onSelect={() => toggleSelect(v.version_no)}
                  onRevert={() => setRevertTarget(v.version_no)}
                />
              ))}
              {versions.length === 0 && (
                <li className="text-xs text-muted-foreground italic px-1">
                  Noch keine gespeicherten Versionen. Sobald du die Strategie aktivierst oder Inhalte änderst, wird automatisch ein Snapshot festgehalten.
                </li>
              )}
            </ul>
          </div>

          {diffPair && (
            <DiffPanel
              left={resolveSnapshot(diffPair[0])}
              right={resolveSnapshot(diffPair[1])}
              leftLabel={diffPair[0] === "current" ? "Aktuell" : `V${diffPair[0]}`}
              rightLabel={diffPair[1] === "current" ? "Aktuell" : `V${diffPair[1]}`}
              onClear={() => setSelected([])}
            />
          )}
        </aside>
      </div>

      <StrategyDialog
        open={editOpen}
        onOpenChange={(o) => { setEditOpen(o); if (!o) load(); }}
        onCreated={() => load()}
        resumeStrategy={editOpen ? strategy : null}
        resumeMode={editMode}
      />

      <AlertDialog open={revertTarget != null} onOpenChange={(o) => !o && setRevertTarget(null)}>
        <AlertDialogContent data-testid="strategy-revert-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Auf Version {revertTarget} zurücksetzen?</AlertDialogTitle>
            <AlertDialogDescription>
              Der aktuelle Stand wird zusätzlich als neue Version festgehalten — nichts geht verloren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="strategy-revert-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRevert}
              disabled={busy}
              data-testid="strategy-revert-confirm-btn"
            >
              <RotateCcw className="mr-2 h-4 w-4" strokeWidth={1.5} /> Zurücksetzen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <>
      <dt className="text-muted-foreground text-xs uppercase tracking-wider self-start pt-0.5">{label}</dt>
      <dd className="whitespace-pre-wrap">{value}</dd>
    </>
  );
}

function VersionRow({ item, isCurrent, isSelected, onSelect, onRevert }) {
  const label = isCurrent ? "Aktuell" : `V${item.version_no}`;
  const savedAt = item.saved_at ? new Date(item.saved_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "";
  const userNote = item.user_note;
  const autoReason = userNote && item.change_note?.includes("—")
    ? item.change_note.split("—")[0].trim()
    : item.change_note;
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        data-testid={`strategy-version-${isCurrent ? "current" : item.version_no}`}
        className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
          isSelected
            ? "border-primary/60 bg-primary/10"
            : "border-border bg-secondary/40 hover:bg-secondary/70"
        }`}
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="rounded-full text-[10px] font-mono">{label}</Badge>
            <span className="text-xs text-muted-foreground">{savedAt}</span>
          </div>
          {onRevert && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRevert(); }}
              className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-primary-foreground inline-flex items-center gap-1"
              data-testid={`strategy-version-revert-${item.version_no}`}
            >
              <RotateCcw className="h-3 w-3" strokeWidth={1.5} /> zurücksetzen
            </button>
          )}
        </div>
        {userNote ? (
          <>
            <div className="text-sm mt-1.5 text-foreground italic">„{userNote}"</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
              {autoReason}
            </div>
          </>
        ) : (
          <div className="text-xs mt-1 text-foreground/80 truncate">{item.change_note}</div>
        )}
      </button>
    </li>
  );
}

function DiffPanel({ left, right, leftLabel, rightLabel, onClear }) {
  const rows = CONTENT_FIELDS.map((f) => {
    const a = left?.[f.key];
    const b = right?.[f.key];
    const changed = !eq(a, b);
    return { ...f, a, b, changed };
  });
  const changedCount = rows.filter((r) => r.changed).length;
  return (
    <div className="card-surface p-5" data-testid="strategy-diff-panel">
      <div className="flex items-center gap-2 mb-3">
        <ArrowRightLeft className="h-4 w-4 text-primary-foreground/80" strokeWidth={1.5} />
        <div className="overline">Vergleich {leftLabel} ↔ {rightLabel}</div>
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground ml-auto"
          data-testid="strategy-diff-clear"
        >
          Auswahl leeren
        </button>
      </div>
      {changedCount === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
          Beide Versionen sind inhaltlich identisch.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.filter((r) => r.changed).map((r) => (
            <div key={r.key} data-testid={`strategy-diff-row-${r.key}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                {r.label}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg border border-[hsl(0_66%_50%/0.4)] bg-[hsl(0_66%_50%/0.08)] p-2 whitespace-pre-wrap">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(0_66%_72%)] mb-0.5">{leftLabel}</div>
                  {fmtValue(r.key, r.a)}
                </div>
                <div className="rounded-lg border border-[hsl(122_39%_49%/0.4)] bg-[hsl(122_39%_49%/0.08)] p-2 whitespace-pre-wrap">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(122_39%_65%)] mb-0.5">{rightLabel}</div>
                  {fmtValue(r.key, r.b)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
